import { useQuery } from '@tanstack/react-query';
import type { DMConversation } from '../lib/api';
import { fetchDMs } from '../lib/api';
import { friendlyPeerName } from '../lib/peerName';
import { EmptyState } from './EmptyState';
import { Skeleton } from './Skeleton';

interface DMsListProps {
  onSelectDM?: (dmId: string) => void;
  selectedDmId?: string | null;
}

export function DMsList({ onSelectDM, selectedDmId }: DMsListProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dms'],
    queryFn: fetchDMs,
    retry: false,
  });

  if (isLoading) {
    return (
      <Skeleton
        className="dms-list-loading"
        ariaLabel="Loading direct messages"
        rows={3}
        variant="list"
      />
    );
  }

  // Treat all errors (incl. 404 — endpoint not yet deployed) as empty, not error.
  if (isError || !data || data.dms.length === 0) {
    return (
      <EmptyState
        className="dms-list-empty"
        icon="💬"
        title="No direct messages yet"
        description="Start a conversation by messaging a contact."
      />
    );
  }

  return (
    <ul className="dms-list" aria-label="Direct messages list">
      {data.dms.map((dm) => (
        <DMRow
          key={dm.dm_id}
          dm={dm}
          isSelected={selectedDmId === dm.dm_id}
          onSelect={() => onSelectDM?.(dm.dm_id)}
        />
      ))}
    </ul>
  );
}

interface DMRowProps {
  dm: DMConversation;
  isSelected: boolean;
  onSelect: () => void;
}

function DMRow({ dm, isSelected, onSelect }: DMRowProps) {
  const isUnread = dm.unread_count > 0;
  const displayName = friendlyPeerName(dm.peer_display_name, dm.peer_username);
  return (
    <li
      className={`dm-row ${isUnread ? 'dm-row-unread' : ''} ${isSelected ? 'dm-row-selected' : ''}`}
    >
      <button
        type="button"
        className="dm-link"
        onClick={onSelect}
        aria-current={isSelected ? 'page' : undefined}
      >
        <span className="dm-peer-name" title={dm.peer_username}>
          {displayName}
        </span>
        <span className="dm-meta">
          {dm.last_message_at && (
            <time className="dm-time" dateTime={dm.last_message_at}>
              {new Date(dm.last_message_at).toLocaleDateString()}
            </time>
          )}
          {/* Unread = bold + accent dot, not a red count (ruled do-not). T-0200. */}
          {isUnread && <span className="dm-unread-dot" aria-label="Unread messages" />}
        </span>
      </button>
    </li>
  );
}
